import { useState } from "react";
import { Link } from "react-router-dom";
import { FileBarChart2, Sparkles } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { useCompany } from "../../components/layout/CompanyLayout";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import ScopeFilters from "../../components/stats/ScopeFilters";
import AnnouncementsFeed from "../../components/comms/AnnouncementsFeed";
import { Card, Kpi, pct, PeriodInfo, RatingChart, SentimentBars, MonthlyChart, GroupTable, Issues, Delays } from "../../components/stats/StatsParts";

const EMPTY = { date_from: "", date_to: "", agency_id: "", service_id: "", category: "" };

const Content = ({ d }) => (
  <div className="space-y-6">
    <PeriodInfo data={d} />
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Kpi testId="dash-kpi-total" label="Feedbacks reçus" value={d.analyzed} />
      <Kpi testId="dash-kpi-avg" label="Note moyenne" value={d.avg_rating} hint="sur 5" />
      <Kpi testId="dash-kpi-response" label="Taux de réponse" value={pct(d.response_rate)} hint={`${d.responded} avec réponse`} />
      <Kpi testId="dash-kpi-resolution" label="Taux de résolution" value={pct(d.resolution_rate)} hint={`${d.resolved} résolu(s) ou clôturé(s)`} />
      <Kpi testId="dash-kpi-open" label="Réclamations ouvertes" value={d.complaints.open} hint="statut nouveau" />
      <Kpi testId="dash-kpi-progress" label="Réclamations en cours" value={d.complaints.in_progress} />
      <Kpi testId="dash-kpi-resolved" label="Réclamations résolues" value={d.complaints.resolved} />
      <Kpi testId="dash-kpi-qr" label="Scans QR" value={d.qr.scans} hint={`${d.qr.feedbacks_from_qr} avis envoyés via QR (scans ≠ avis)`} />
    </div>
    <div className="grid gap-6 lg:grid-cols-3">
      <Card title="Répartition des notes" testId="dash-rating-chart"><RatingChart byRating={d.by_rating} /></Card>
      <Card title="Positifs, neutres, négatifs" testId="dash-sentiment"><SentimentBars sentiment={d.sentiment} /></Card>
      <Card title="Délais moyens de traitement" testId="dash-delays"><Delays delays={d.delays} /></Card>
    </div>
    <Card title="Évolution mensuelle" testId="dash-monthly"><MonthlyChart monthly={d.monthly} /></Card>
    <div className="grid gap-6 lg:grid-cols-3">
      <Card title="Par agence" testId="dash-by-agency"><GroupTable rows={d.by_agency} label="Agence" testId="table-agencies" /></Card>
      <Card title="Par service" testId="dash-by-service"><GroupTable rows={d.by_service} label="Service" testId="table-services" /></Card>
      <Card title="Motifs récurrents d'insatisfaction" testId="dash-issues"><Issues items={d.recurring_issues} /></Card>
    </div>
  </div>
);

export default function Dashboard() {
  const { company, can } = useCompany();
  const [scope, setScope] = useState(EMPTY);
  const agencies = useApi("/company/agencies");
  const services = useApi("/company/services");
  const qs = new URLSearchParams(Object.entries(scope).filter(([, v]) => v)).toString();
  const { data, loading, error, reload } = useApi(`/company/analytics?${qs}`);
  return (
    <div>
      <PageHeader eyebrow={company.name} title="Tableau de bord" description="Indicateurs calculés à partir des avis réellement soumis à votre organisation."
        actions={can("reports:read") && <div className="flex flex-wrap gap-2"><Button asChild variant="outline" className="rounded-full" data-testid="dashboard-insights-link"><Link to="/app/analyse-ia"><Sparkles className="mr-1.5 h-4 w-4" />Analyse intelligente</Link></Button><Button asChild variant="outline" className="rounded-full" data-testid="dashboard-reports-link"><Link to="/app/rapports"><FileBarChart2 className="mr-1.5 h-4 w-4" />Rapports</Link></Button></div>} />
      <div className="mb-6"><AnnouncementsFeed url="/company/platform-announcements" track={false} testId="platform-announcements" /></div>
      <ScopeFilters value={scope} onChange={setScope} agencies={agencies.data} services={services.data} />
      {loading && !data && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && <div className={loading ? "opacity-60" : ""}><Content d={data} /></div>}
    </div>
  );
}
